import { useState, useMemo, useEffect, useRef } from "react";
import { useMutation, useInfiniteQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button"; // Assuming shadcn/ui Button
import { Card } from "@/components/ui/card"; // Assuming shadcn/ui Card
import {
  Search,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useTRPC, useTRPCClient } from "@/trpc"; // Assuming tRPC setup
import { JobPostProvider } from "../matching/job-post-provider";
import { MatchStrengthBadge } from "../components/match-strength-badge";
import { EmploymentType, PracticeSetting } from "@api/shared-runtime/enums";
import { queryClient } from "@/ajax/queryClient";
import {
  showNotInterestedToast,
  showNotInterestedErrorToast,
} from "../utils/show-not-interested-toast";
import { JobAcceptDialog, type AcceptableJob } from "../job-accept-dialog";
import { JobPostRemoveInterestDialog } from "../job-post-remove-interest-dialog";
import { JobSearchFilters } from "./components/job-search-filters";
import { useProviderDefaultJobFilters } from "./hooks/use-provider-default-job-filters";

export default function JobPosts({
  searchParams,
  onSearchParamsChange,
}: {
  searchParams: {
    specialtyIds?: string;
    professionalTypeId?: string;
    showRejected?: string;
  };
  onSearchParamsChange: (params: {
    specialtyIds?: string;
    professionalTypeId?: string;
    showRejected?: string;
  }) => void;
}) {
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const PAGE_SIZE = 20;

  // --- State for Filters ---
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const [specialtyFilter, setSpecialtyFilter] = useState<
    {
      name: string;
      id: string;
    }[]
  >([]);
  const [practiceSettingFilter, setPracticeSettingFilter] = useState<
    PracticeSetting[]
  >([]);
  const [professionalTypeFilter, setProfessionalTypeFilter] = useState<
    { name: string; id: string } | undefined
  >(undefined);
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState<
    EmploymentType[]
  >([]);
  const [showRejectedJobs, setShowRejectedJobs] = useState<boolean>(false);
  const [includeRemote, setIncludeRemote] = useState<boolean>(true);
  const [includeExternalJobs, setIncludeExternalJobs] = useState<boolean>(true);

  // Pre-fill the search with the provider's own professional type, specialties,
  // and licensed states. We seed the filter state once (the first time the
  // profile data resolves) so we never clobber edits the provider makes after.
  const providerDefaults = useProviderDefaultJobFilters();
  const [defaultsInitialized, setDefaultsInitialized] =
    useState<boolean>(false);

  useEffect(() => {
    if (defaultsInitialized || providerDefaults.isLoading) {
      return;
    }
    if (providerDefaults.professionalType) {
      setProfessionalTypeFilter(providerDefaults.professionalType);
    }
    if (providerDefaults.specialties.length > 0) {
      setSpecialtyFilter(providerDefaults.specialties);
    }
    if (providerDefaults.stateCodes.length > 0) {
      setStateFilter(providerDefaults.stateCodes);
    }
    setDefaultsInitialized(true);
  }, [defaultsInitialized, providerDefaults]);

  const { mutateAsync: setDecision } = useMutation(
    trpc.jobPost.setDecision.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["jobPost.search"],
        });
      },
    }),
  );

  const [acceptedJob, setAcceptedJob] = useState<AcceptableJob | null>(null);
  const [removeInterestJobId, setRemoveInterestJobId] = useState<string | null>(
    null,
  );
  const [removeInterestPreviousDecision, setRemoveInterestPreviousDecision] =
    useState<boolean | null>(null);

  // --- Memoize query params for stable reference ---
  const queryParams = useMemo(
    () => ({
      specialtyIds: specialtyFilter.map((s) => s.id),
      healthcareProfessionalTypeId: professionalTypeFilter?.id,
      practiceSettingIds: practiceSettingFilter,
      showRejectedJobs: showRejectedJobs,
      stateCodes: stateFilter,
      includeRemote: includeRemote,
      employmentTypeIds: employmentTypeFilter,
      includeExternalJobs: includeExternalJobs,
    }),
    [
      specialtyFilter,
      professionalTypeFilter?.id,
      practiceSettingFilter,
      showRejectedJobs,
      stateFilter,
      includeRemote,
      employmentTypeFilter,
      includeExternalJobs,
    ],
  );

  // --- Fetch Job Data with Infinite Query ---
  const {
    data: infiniteData,
    isLoading,
    error: _error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["jobPost.search", queryParams],
    queryFn: ({ pageParam = 0 }) =>
      trpcClient.jobPost.search.query({
        ...queryParams,
        offset: pageParam * PAGE_SIZE,
        limit: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.hasMore) {
        return allPages.length;
      }
      return undefined;
    },
    initialPageParam: 0,
    enabled: defaultsInitialized,
  });

  // --- Flatten paginated data ---
  const jobs = useMemo(() => {
    return infiniteData?.pages.flatMap((page) => page.jobs) ?? [];
  }, [infiniteData]);

  // --- IntersectionObserver for infinite scroll ---
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // --- Event Handlers ---
  const handleInterestSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ["jobPost.search"],
    });
  };

  const handleInterestChange = async ({
    jobId,
    isInterested,
  }: {
    jobId: string;
    isInterested: boolean | null;
  }) => {
    if (isInterested === false) {
      try {
        await setDecision({ id: jobId, interested: false });
        showNotInterestedToast();
      } catch {
        showNotInterestedErrorToast();
      }
      return;
    }
    const job = jobs?.find((j) => j.id === jobId);
    if (isInterested === true) {
      setAcceptedJob({
        id: jobId,
        externalUrl: job?.externalUrl ?? null,
        eligibility: job?.eligibility,
      });
      return;
    }
    if (isInterested === null) {
      setRemoveInterestJobId(jobId);
      setRemoveInterestPreviousDecision(job?.decision?.interested ?? null);
      return;
    }
  };

  const clearFilters = () => {
    setSpecialtyFilter([]);
    setProfessionalTypeFilter(undefined);
    setStateFilter([]);
    setPracticeSettingFilter([]);
    setEmploymentTypeFilter([]);
    setShowRejectedJobs(false);
    setIncludeRemote(true);
    setIncludeExternalJobs(true);
  };

  // --- Active filter count (for badges/disabled states) ---
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (specialtyFilter.length > 0) {
      count += 1;
    }
    if (professionalTypeFilter) {
      count += 1;
    }
    if (stateFilter.length > 0) {
      count += 1;
    }
    if (practiceSettingFilter.length > 0) {
      count += 1;
    }
    if (employmentTypeFilter.length > 0) {
      count += 1;
    }
    if (showRejectedJobs === true) {
      count += 1;
    }
    if (includeRemote === false) {
      count += 1;
    }
    if (includeExternalJobs === false) {
      count += 1;
    }
    return count;
  }, [
    specialtyFilter,
    professionalTypeFilter,
    stateFilter,
    practiceSettingFilter,
    employmentTypeFilter,
    showRejectedJobs,
    includeRemote,
    includeExternalJobs,
  ]);

  // --- Render Logic ---
  return (
    <div className="min-h-screen bg-slate-50 font-inter">
      <div className="container mx-auto px-0 sm:px-6 py-4 sm:py-6 max-w-7xl">
        {/* Mobile Filter Toggle Button */}
        <div className="lg:hidden mb-4">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="w-full gap-2 sm:w-auto"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">
                {activeFilterCount}
              </span>
            )}
            {showFilters ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Mobile Filters Section */}
        {showFilters && (
          <Card className="lg:hidden mb-6 p-4 bg-white border border-slate-200 rounded-2xl">
            <JobSearchFilters
              hideTitle
              professionalTypeFilter={professionalTypeFilter}
              setProfessionalTypeFilter={setProfessionalTypeFilter}
              specialtyFilter={specialtyFilter}
              setSpecialtyFilter={setSpecialtyFilter}
              stateFilter={stateFilter}
              setStateFilter={setStateFilter}
              practiceSettingFilter={practiceSettingFilter}
              setPracticeSettingFilter={setPracticeSettingFilter}
              employmentTypeFilter={employmentTypeFilter}
              setEmploymentTypeFilter={setEmploymentTypeFilter}
              includeRemote={includeRemote}
              setIncludeRemote={setIncludeRemote}
              showRejectedJobs={showRejectedJobs}
              setShowRejectedJobs={setShowRejectedJobs}
              includeExternalJobs={includeExternalJobs}
              setIncludeExternalJobs={setIncludeExternalJobs}
              activeFilterCount={activeFilterCount}
              onReset={clearFilters}
            />
          </Card>
        )}

        {/* Main Content Layout */}
        <div className="lg:flex lg:gap-6">
          {/* Desktop Sidebar Filters */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <div className="sticky top-6">
              <Card className="rounded-2xl border border-slate-200 bg-white p-5">
                <JobSearchFilters
                  professionalTypeFilter={professionalTypeFilter}
                  setProfessionalTypeFilter={setProfessionalTypeFilter}
                  specialtyFilter={specialtyFilter}
                  setSpecialtyFilter={setSpecialtyFilter}
                  stateFilter={stateFilter}
                  setStateFilter={setStateFilter}
                  practiceSettingFilter={practiceSettingFilter}
                  setPracticeSettingFilter={setPracticeSettingFilter}
                  employmentTypeFilter={employmentTypeFilter}
                  setEmploymentTypeFilter={setEmploymentTypeFilter}
                  includeRemote={includeRemote}
                  setIncludeRemote={setIncludeRemote}
                  showRejectedJobs={showRejectedJobs}
                  setShowRejectedJobs={setShowRejectedJobs}
                  includeExternalJobs={includeExternalJobs}
                  setIncludeExternalJobs={setIncludeExternalJobs}
                  activeFilterCount={activeFilterCount}
                  onReset={clearFilters}
                />
              </Card>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:flex-1 lg:min-w-0">
            {/* Info Note - More compact */}
            {jobs && jobs.length > 0 && (
              <p className="mb-4 text-sm text-slate-500">
                New positions are added daily — check back regularly.
              </p>
            )}
            {/* Job List */}
            <div className="space-y-4 pb-12">
              {(isLoading || !defaultsInitialized) && (
                <div className="flex justify-center items-center py-16">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
                    <p className="text-slate-600 font-medium">
                      Finding opportunities...
                    </p>
                  </div>
                </div>
              )}
              {jobs &&
                jobs.length > 0 &&
                jobs.map((job) => (
                  <JobPostProvider
                    key={job.id}
                    jobData={job}
                    onInterestChange={handleInterestChange}
                    enableSwipe={false}
                    titleAs="h2"
                    headerAccessory={<MatchStrengthBadge match={job.match} />}
                  />
                ))}
              {jobs &&
                jobs.length === 0 &&
                !isLoading &&
                defaultsInitialized && (
                  <div className="text-center py-12 px-6 bg-white rounded-2xl shadow-sm border border-slate-200">
                    <div className="bg-primary/5 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Search className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      No Matching Positions
                    </h3>
                    <p className="text-slate-600 mb-6 max-w-md mx-auto">
                      Adjust your filters to discover more opportunities.
                    </p>
                    <Button
                      onClick={clearFilters}
                      disabled={activeFilterCount === 0}
                    >
                      Clear filters
                    </Button>
                  </div>
                )}

              {/* Load More Trigger */}
              {hasNextPage && (
                <div
                  ref={loadMoreRef}
                  className="flex items-center justify-center py-6"
                >
                  {isFetchingNextPage ? (
                    <div className="flex items-center space-x-2 text-slate-500">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-300 border-t-primary"></div>
                      <span className="text-sm font-medium">
                        Loading more jobs...
                      </span>
                    </div>
                  ) : (
                    <div className="h-4" /> // Invisible trigger for intersection observer
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {acceptedJob && (
        <JobAcceptDialog
          job={acceptedJob}
          onClose={() => setAcceptedJob(null)}
          onSuccess={handleInterestSuccess}
        />
      )}
      {removeInterestJobId && (
        <JobPostRemoveInterestDialog
          onClose={() => {
            setRemoveInterestJobId(null);
            setRemoveInterestPreviousDecision(null);
          }}
          onSuccess={handleInterestSuccess}
          jobId={removeInterestJobId}
          previousDecision={removeInterestPreviousDecision ?? undefined}
        />
      )}
    </div>
  );
}
